import { ReactNode, ReactElement } from 'react';
import { FormControl, FormLabel, RadioGroup, RadioGroupProps, FormHelperText } from '@mui/material';
import { Control, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type FormRadioGroupProps<TFieldValues> = RadioGroupProps & {
  control?: Control<TFieldValues>;
  controlName: Path<TFieldValues>;
  formLabel: string;
  required?: boolean;
  children?: ReactNode[];
};

export function FormRadioGroup<TFieldValues>({ ...props }: FormRadioGroupProps<TFieldValues>): ReactElement {
  const { t } = useTranslation();

  const { controlName, control, required, formLabel, ...radioProps } = props;

  const requiredHelperText = `*${t('common.required-helper-text')}`;
  const requiredError = t('common.required-error-text');

  return (
    <Controller
      name={controlName}
      control={control}
      rules={{ required: required }}
      render={({ field, fieldState }) => {
        let helperText: string | undefined = undefined;
        if (required) {
          helperText = fieldState.invalid ? requiredError : requiredHelperText;
        }

        return (
          <FormControl component="fieldset" sx={{ paddingBottom: '16px' }}>
            <FormLabel error={fieldState.invalid} component="legend">
              {formLabel}
            </FormLabel>
            <RadioGroup {...field} {...radioProps}>
              {props.children}
            </RadioGroup>
            <FormHelperText error={fieldState.invalid} sx={{ marginLeft: 0 }} id="component-error-text">
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}
